import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Form, Input, Select, Typography } from "antd";
import { Truck, TruckTrailers, TruckEntry, FileNotesDocument, Document } from "assets/icons";
import AddDocument from "components/document";
import Loading from "components/drawerLoading";
import AddTrailersPlates from "components/trailersPlates";
import { bindActionCreators, Dispatch } from "redux";
import { Creators as LobbyActions } from "store/lobby/lobby-duck";
import { IDocumentsData, IProcessesData, IRegisterActiveItem, PlateFormatType as IPlateFormatType } from "store/lobby/lobby-model";
import { checkIfIsEqualInboundProcess, checkPlateFormatType, maxLengthPlateFormatType } from "utils/form/validations";
import { checkIfAddDocumentsByProcessId, getDocumentLabel, searchProcessCorrelationIdSelectedById } from "utils/functions";
import useAfrDocumentsFlow from "utils/hooks/useAfrDocumentsFlow";
import useTranslation from "utils/hooks/useTranslation";
import * as S from "../styled/item.styled";
import DocumentsList from "./documentsList";

type LayoutType = Parameters<typeof Form>[0]["layout"];

export interface ICheckoutForm {
  formCheckout?: any;
  drawerIsLoading: boolean;
  isLoading?: boolean;
  registerActive?: IRegisterActiveItem;
  outboundProcessesList: Array<IProcessesData>;
  visibleDrawerOutbound: boolean;
  documentsList: Array<IDocumentsData>;
  plateFormatType: IPlateFormatType;
}

interface IDispatchProps {
  lobbyOutboundProcessActive: (...args) => void;
  lobbyDocumentsFetchAsync: (...args) => void;
}

export type IProps = ICheckoutForm & IDispatchProps;

const CheckoutForm = ({
  formCheckout,
  registerActive,
  drawerIsLoading,
  outboundProcessesList,
  documentsList,
  visibleDrawerOutbound,
  plateFormatType,
  lobbyOutboundProcessActive,
  lobbyDocumentsFetchAsync,
}: IProps): JSX.Element => {
  const isAfrDocumentsFlow = useAfrDocumentsFlow();
  const [formLayout, setFormLayout] = useState<LayoutType>("vertical");
  const [fieldsCount, setFieldsCount] = useState(1);
  const [visibleDocuments, setVisibleDocuments] = useState(false);
  const INVOICE_INITIALS = useTranslation({ id: "invoiceInitials" });
  const CUSTOM_PLATE_DESCRIPTION_INFORMATION = useTranslation({ id: "custom_plate_description_information" });
  const BRAZIL_MERCOSUR_PLATE_DESCRIPTION_INFORMATION = useTranslation({ id: "brazil_mercosur_plate_description_information" });
  const MEXICO_PLATE_DESCRIPTION_INFORMATION = useTranslation({ id: "mexico_plate_description_information" });
  const outboundProcess = outboundProcessesList?.map((process) => process.processId);
  const eventType = 2;
  const onFormLayoutChange = ({ layout }: { layout: LayoutType }) => {
    setFormLayout(layout);
  };

  const isHiddenInvoiceField = () => !visibleDocuments;

  const handleChangeProcess = (processDescription) => {
    lobbyOutboundProcessActive(processDescription);
    const processCorrelationId = searchProcessCorrelationIdSelectedById(processDescription, outboundProcessesList);
    lobbyDocumentsFetchAsync(processCorrelationId, eventType);
    setVisibleDocuments(true);
  };

  useEffect(() => {
    if (visibleDrawerOutbound && registerActive) {
      setVisibleDocuments(false);
      if (outboundProcess.includes(registerActive.processId)) {
        setVisibleDocuments(true);
        const processCorrelationIdActive = searchProcessCorrelationIdSelectedById(registerActive?.processId, outboundProcessesList);
        processCorrelationIdActive && lobbyDocumentsFetchAsync(processCorrelationIdActive, eventType);
      }
      checkIfAddDocumentsByProcessId(outboundProcess, registerActive, documentsList, formCheckout);
    }
  }, [visibleDrawerOutbound, registerActive]);

  useEffect(() => {
    if (registerActive) {
      lobbyOutboundProcessActive(registerActive?.processId);
      formCheckout.resetFields();
      formCheckout.setFieldsValue({
        observation: "",
        trailersPlates: [""],
        processDescription: checkIfIsEqualInboundProcess(outboundProcess, registerActive),
      });
    }
  }, [registerActive]);

  const { Title } = Typography;
  const autoSizeInputText = { minRows: 4, maxRows: 13 };

  const validationPlate = checkPlateFormatType(plateFormatType);

  const PLATE_DESCRIPTIONS: Record<IPlateFormatType, string> = {
    0: BRAZIL_MERCOSUR_PLATE_DESCRIPTION_INFORMATION,
    1: CUSTOM_PLATE_DESCRIPTION_INFORMATION,
    2: MEXICO_PLATE_DESCRIPTION_INFORMATION,
  };

  const translateDescriptionInformationPlate = (plateFormatType: IPlateFormatType): string => PLATE_DESCRIPTIONS[plateFormatType] || PLATE_DESCRIPTIONS[0];

  return (
    <Loading show={drawerIsLoading}>
      <Form style={S.FormLayout} layout={"vertical"} form={formCheckout} initialValues={{ layout: formLayout }} onValuesChange={onFormLayoutChange} data-testid="form-checkout">
        <S.LayoutForm>
          <div className="item">
            <Title style={S.TitleForm} level={4}>
              {useTranslation({ id: "input_data" })}
            </Title>
            <S.LabelCarrier>
              <div>
                <TruckEntry className="truckEntryIcon" />
              </div>
              <S.LabelInfo>
                <div style={S.TitleItem}>{useTranslation({ id: "shipping_company" })}</div>
                <S.LabelCarrierDescription>{registerActive?.carrier}</S.LabelCarrierDescription>
              </S.LabelInfo>
            </S.LabelCarrier>
            <S.LabelInfoItem>
              <div>
                <Truck className="truckIcon" />
              </div>
              <S.LabelInfo>
                <div style={S.TitleItem}>{useTranslation({ id: "plate" })}</div>
                <S.LabelItemDescription>{registerActive?.vehiclePlate}</S.LabelItemDescription>
              </S.LabelInfo>
            </S.LabelInfoItem>
            <S.LabelInfoItem>
              <div>
                <TruckTrailers className="truckIcon" />
              </div>
              <S.LabelInfo>
                <div style={S.TitleItem}>{useTranslation({ id: "trailers_plates" })}</div>
                <S.LabelItemDescription>{registerActive?.trailersPlates?.length === 0 ? "-" : registerActive?.trailersPlates?.join(" - ")}</S.LabelItemDescription>
              </S.LabelInfo>
            </S.LabelInfoItem>
            <S.LabelInfoItem>
              <div>
                <FileNotesDocument className="processIcon" />
              </div>
              <S.LabelInfo>
                <div style={S.TitleItem}>{useTranslation({ id: "processes" })}</div>
                <S.LabelProcessDescription>{registerActive?.processDescription || "-"}</S.LabelProcessDescription>
              </S.LabelInfo>
            </S.LabelInfoItem>
            <S.LabelInfoItem>
              <div>
                <Document className="documentIcon" />
              </div>
              <S.LabelInfo>
                <div style={S.TitleItem}>{useTranslation({ id: "documents" })}</div>
                <S.LabelProcessDescription>
                  {registerActive?.invoices?.map(
                    (invoice, index) =>
                      invoice.length !== 0 && (
                        <p key={index} className="documentItem">
                          {INVOICE_INITIALS} {invoice}
                        </p>
                      )
                  )}
                  <DocumentsList documents={registerActive?.documents || []} />
                  {registerActive?.documents?.length !== 0 || "-"}
                </S.LabelProcessDescription>
              </S.LabelInfo>
            </S.LabelInfoItem>
            <S.LabelInfoItem>
              <div style={{ alignItems: "center" }}>
                <div style={S.TitleItem}>{useTranslation({ id: "observation" })}</div>
                <S.LabelObservationDescription>{registerActive?.observation || "-"}</S.LabelObservationDescription>
              </div>
            </S.LabelInfoItem>
          </div>
          <div className="divider" />
          <div className="item">
            <Title style={S.TitleForm} level={4}>
              {useTranslation({ id: "checkout" })}
            </Title>
            <S.FormItem
              name="processDescription"
              label={`${useTranslation({ id: "processes" })}*`}
              style={S.ProcessesFormItem}
              rules={[
                {
                  required: true,
                  message: useTranslation({ id: "required_field" }),
                },
              ]}
            >
              <Select size="large" style={S.ProcessesSelect} onChange={handleChangeProcess}>
                {outboundProcessesList?.length > 0 &&
                  outboundProcessesList?.map((process, index) => (
                    <Select.Option value={process.processId} key={index}>
                      {process.description}
                    </Select.Option>
                  ))}
              </Select>
            </S.FormItem>
            {visibleDocuments && (
              <>
                {documentsList?.map((document) => (
                  <AddDocument
                    id={document.id}
                    key={document.id}
                    eventType={eventType}
                    hidden={isHiddenInvoiceField()}
                    label={getDocumentLabel(document.description, isAfrDocumentsFlow)}
                    fieldName={document.description}
                    disabled={false}
                    allowLetters={document.maskRules.allowLetters}
                    allowNumbers={document.maskRules.allowNumbers}
                    allowSpecialCharacters={document.maskRules.allowSpecialCharacters}
                    minCharacters={document.maskRules.minCharacters}
                    maxCharacters={document.maskRules.maxCharacters}
                    required={document.required}
                    isIntegration={false}
                    form={formCheckout}
                  />
                ))}
              </>
            )}
            <S.FormItem
              label={`${useTranslation({ id: "trailers_plates" })}`}
              tooltip={
                <>
                  {useTranslation({ id: "description_information_add_plates" })}
                  <br />
                  {translateDescriptionInformationPlate(plateFormatType)}
                </>
              }
              rules={[
                {
                  required: false,
                },
              ]}
            >
              <Form.List name="trailersPlates">
                {(fields, { add }, { errors }) => {
                  return (
                    <AddTrailersPlates
                      fields={fields}
                      setFieldsCount={setFieldsCount}
                      fieldsCount={fieldsCount}
                      errors={errors}
                      add={add}
                      disabled={false}
                      maxLength={maxLengthPlateFormatType(plateFormatType)}
                      plateFormatType={plateFormatType}
                      validationPlate={validationPlate}
                    />
                  );
                }}
              </Form.List>
            </S.FormItem>
            <S.FormItem label={useTranslation({ id: "observation" })} name="observation" data-testid="observation_field">
              <Input.TextArea maxLength={500} autoSize={autoSizeInputText} />
            </S.FormItem>
          </div>
          <input data-testid="change-process" onChange={(e) => handleChangeProcess(e.target.value)} style={S.FakeBtnConfirm} />
        </S.LayoutForm>
      </Form>
    </Loading>
  );
};

const mapDispatchToActions = (dispatch: Dispatch) => bindActionCreators({ ...LobbyActions }, dispatch);

const mapStateToProps = (state: any) => ({
  isLoading: state.Lobby.isLoading,
  registerActive: state.Lobby.registerActive,
  outboundProcessesList: state.Lobby.outboundProcessesList,
  documentsList: state.Lobby.documentsList,
  visibleDrawerOutbound: state.Lobby.visibleDrawerOutbound,
  drawerIsLoading: state.Lobby.drawerIsLoading,
  plateFormatType: state.Lobby.plateFormatType,
});

export default connect(mapStateToProps, mapDispatchToActions)(CheckoutForm);
